import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { StaffUser } from "../../lib/auth";
import {
  EMPTY_SIGNALS,
  getShellSignals,
  openShellSignalsStream,
  type ShellSignals,
  type ShellStream,
} from "./signals";

/**
 * How often to re-read when there is no live stream.
 *
 * This is the fallback, not the mechanism. It is the old behaviour kept alive
 * for the cases a stream cannot be had - an ancient browser, a proxy that eats
 * event streams, a server holding its connection cap - and it is one request
 * now rather than the nine it used to be.
 */
const POLL_MS = 60_000;

/**
 * How long a hidden tab keeps its stream before letting go.
 *
 * A stream holds one of the browser's six connections to this origin for as
 * long as it is open, and the CRM is served over HTTP/1.1 (see the vhost in
 * deployment/husly/nginx-husly.conf). Four abandoned tabs would therefore
 * starve the fifth of sockets to actually load a page with. Five minutes is
 * long enough that switching to another tab and back costs nothing, and short
 * enough that yesterday's forgotten tab is not still holding a socket.
 */
const IDLE_DISCONNECT_MS = 5 * 60_000;

/**
 * Dropped connections inside {@link DROP_WINDOW_MS} before giving up on the
 * stream and falling back to polling.
 *
 * The browser reconnects a dropped event stream by itself, which is right for a
 * flaky minute and wrong for a proxy that closes every attempt: that would be a
 * silent reconnect loop with a shell that never updates. Counting the drops
 * turns the second case into the fallback and leaves the first one alone.
 */
const MAX_DROPS = 4;
const DROP_WINDOW_MS = 60_000;

interface SignalsValue extends ShellSignals {
  /** Refetch now - used after an action the operator knows changed a counter.
   *  With a live stream the server usually says so first; this is for the
   *  moment when it has not yet. */
  refresh: () => void;
  /** True while the push connection is up. The shell does not draw this today;
   *  it exists so that "the badges are live" is a fact a component can ask
   *  about rather than assume. */
  live: boolean;
}

/** Exported so a preview or a test can mount the shell's consumers with canned
 *  signals instead of a live API. */
export const SignalsContext = createContext<SignalsValue>({ ...EMPTY_SIGNALS, refresh: () => {}, live: false });

export function useSignals(): SignalsValue {
  return useContext(SignalsContext);
}

/**
 * Keeps the shell's counters true without anybody reloading the page.
 *
 * The counters arrive over a server-sent event stream: the API pushes the whole
 * payload the moment a CRM write lands (see `crm-dashboard/shell/routes.ts`), so
 * a call that comes in while the operator is reading a ticket puts a number on
 * the bell within a second, and clearing it in one tab clears it in the other.
 *
 * Three things can go wrong and each has an answer rather than a shrug:
 *
 *   THE STREAM CANNOT BE HAD. Old browser, hostile proxy, server at its
 *   connection cap. Falls back to the one-request poll, which is what the shell
 *   did before any of this existed - degraded, never broken.
 *
 *   THE MACHINE WAS ASLEEP. A resumed laptop has an open-looking stream that
 *   received nothing for an hour, so coming back to the foreground always costs
 *   one plain read; the stream is trusted for what happens next, never for what
 *   happened while nobody was watching.
 *
 *   THE TAB WAS ABANDONED. A stream held open in a tab nobody has looked at for
 *   five minutes is a wasted socket on an origin that only has six; it is
 *   dropped and re-opened on the way back in.
 */
export function SignalsProvider({ user, children }: { user: StaffUser; children: ReactNode }) {
  const [data, setData] = useState<ShellSignals>(EMPTY_SIGNALS);
  const [live, setLive] = useState(false);
  /** Bumped by `refresh()`; read by the effect so a manual refetch does not
   *  have to reach into it. */
  const readNowRef = useRef<() => void>(() => {});

  const refresh = useCallback(() => readNowRef.current(), []);

  useEffect(() => {
    let stopped = false;
    let stream: ShellStream | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let drops: number[] = [];

    const apply = (next: ShellSignals) => {
      if (!stopped) setData(next);
    };

    const readNow = () => {
      getShellSignals()
        .then(apply)
        .catch(() => {
          // A failed read leaves the last known counters on screen. Blanking the
          // rail because one request timed out would read as "nothing to do".
        });
    };
    readNowRef.current = readNow;

    const dropStream = () => {
      stream?.close();
      stream = null;
      if (!stopped) setLive(false);
    };

    const startPolling = () => {
      if (stopped) return;
      // Unconditionally, before the early return below: giving up a second time
      // has to close the second connection too, or a failed retry would sit
      // there as a non-null `stream` and block every retry after it.
      dropStream();
      if (pollTimer) return;
      pollTimer = setInterval(() => {
        if (document.visibilityState !== "visible") return;
        readNow();
        // And try to get the stream back. A server that was restarting when we
        // gave up is usually answering again by now, and this is the cheapest
        // possible retry: it costs one connection attempt per minute and stops
        // the moment it succeeds.
        openStream();
      }, POLL_MS);
    };

    const stopPolling = () => {
      if (!pollTimer) return;
      clearInterval(pollTimer);
      pollTimer = null;
    };

    function openStream(): void {
      if (stopped || stream) return;
      drops = [];
      stream = openShellSignalsStream({
        onData: (next) => {
          drops = [];
          stopPolling();
          if (!stopped) setLive(true);
          apply(next);
        },
        onError: (permanent) => {
          if (stopped) return;
          setLive(false);
          const now = Date.now();
          drops = drops.filter((at) => now - at < DROP_WINDOW_MS);
          drops.push(now);
          // A refused connection is closed for good and the browser will not
          // retry it; a dropped one it retries by itself, and only a run of
          // those means the stream is not going to work here.
          if (permanent || drops.length >= MAX_DROPS) startPolling();
        },
      });
    }

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        if (idleTimer) {
          clearTimeout(idleTimer);
          idleTimer = null;
        }
        // Always a plain read on the way in: whatever the stream's state says,
        // a machine that was asleep missed everything that happened.
        readNow();
        openStream();
        return;
      }
      if (!idleTimer) {
        idleTimer = setTimeout(dropStream, IDLE_DISCONNECT_MS);
      }
    };

    readNow();
    openStream();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVisibility);
      dropStream();
      stopPolling();
      if (idleTimer) clearTimeout(idleTimer);
    };
    // Keyed on the operator rather than on nothing: the payload is per-user
    // (the call cursors are), so a different person signing in on this tab has
    // to get a different stream.
  }, [user.id]);

  const value = useMemo<SignalsValue>(() => ({ ...data, refresh, live }), [data, refresh, live]);
  return <SignalsContext.Provider value={value}>{children}</SignalsContext.Provider>;
}
